import { defineComponent, ref, Ref, computed } from 'vue';
import context, { ArrayResultModel } from '@truckdown/systems';
import { AccountModel, AccountStatus } from '@truckdown/accounts';
import { FindLocationsUsingAccounts, LocationModel, LocationStatus, ILocationQueryService, FindLocationSortOptions } from '@truckdown/locations';
import { isFleet, isGeneralAdmin, showMessage } from "../Utilities/Utilities";
import editCompanyAddress from "./EditCompanyAddress/EditCompanyAddress.vue";
import editCompanyInfo from "./EditCompanyInformation/EditCompanyInfo.vue";
import editFleetInfo from "./EditFleetInfo/EditFleetInfo.vue";

const resultsPerPage = 15;

interface IData {
    isLoading: Ref<boolean>,
    account: Ref<AccountModel>,
    isFleet: Ref<boolean>,
    isGeneralAdmin: Ref<boolean>,
    resetList: Ref<boolean>,

    showEditCompanyAddress: Ref<boolean>,
    showEditCompanyInfo: Ref<boolean>,
    showEditFleetInfo: Ref<boolean>,

    locations: Ref<ArrayResultModel<LocationModel>>,
    locationsCount: Ref<number>,
    pageCount: Ref<number>,
    currentQuery: Ref<FindLocationsUsingAccounts>,
}

const getInitialData = function (props: any) {
    let query = {
        accountIds: [props.account.id],
        limit: resultsPerPage,
        tags: ["!#inactive", "!#purge"],
        sortOption: FindLocationSortOptions.CityAscending
    };

    return {
        isLoading: ref<boolean>(false),
        account: ref<AccountModel>(props.account),
        isFleet: ref<boolean>(isFleet()),
        isGeneralAdmin: ref<boolean>(isGeneralAdmin()),
        resetList: ref<boolean>(false),

        showEditCompanyAddress: ref<boolean>(false),
        showEditCompanyInfo: ref<boolean>(false),
        showEditFleetInfo: ref<boolean>(false),

        locations: ref<ArrayResultModel<LocationModel>>(props.locations),
        locationsCount: ref<Number>(props.locations.count || 0),
        pageCount: ref<number>(resultsPerPage),
        currentQuery: ref<FindLocationsUsingAccounts>(query)
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        lastPagerObject: computed(() => {
            if (data.locations.value && data.locations.value.results && data.locations.value.results.length > 0) {
                let last = (data.locations.value.results)[data.locations.value.results.length - 1];
                return {
                    lastId: last.id,
                    lastCity: last.address.city,
                    lastRegion: last.address.regionCode,
                    lastCountry: last.address.countryCode
                }
            }
        })
    }
}

interface IPageKey {
    lastId?: string,
    lastCity?: string,
    lastRegion?: string,
    lastCountry?: string
}

const closeModals = function (data: IData) {
    data.showEditCompanyAddress.value = false;
    data.showEditCompanyInfo.value = false;
    data.showEditFleetInfo.value = false;
}

const getMethods = function (data: IData, props: any) {
    return {
        updateList: async function (updateArgs?: IPageKey) {
            await loadLocations(data, data.currentQuery.value, updateArgs);
        },
        getStatus: function (account: AccountModel) {
            return AccountStatus[account.status];
        },
        getListingStatus: function (location: LocationModel) {
            return LocationStatus[location.status];
        },
        getHasEdit: function (location: LocationModel) {
            if (location.edit && location.edit != null) {
                return "Yes"
            }
            return "";
        },
        formatPhoneString: function (phone: string) {
            if (phone?.length == 10) {
                phone = phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
            }
            return phone;
        },
        getLocationDisplay: function (location: LocationModel) {
            return `${location.name} (${location.address.city}, ${location.address.regionCode})`;
        },
        getStreetAddress: function (location: LocationModel) {
            return `${location.address.line1}, ${location.address.city}, ${location.address.regionCode}`;
        },
        cancelEditCompanyAddress: function () {
            data.showEditCompanyAddress.value = false;
        },
        cancelEditCompanyInfo: function () {
            data.showEditCompanyInfo.value = false;
        },
        cancelEditFleetInfo: function () {
            data.showEditFleetInfo.value = false;
        },
        update: function (account: AccountModel) {
            data.account.value = account;
            closeModals(data);
        }
    }
}

const loadLocations = async function (data: IData, query: FindLocationsUsingAccounts, page?: IPageKey) {
    if (!data.isLoading.value && data.account.value.id && !isFleet()) {
        data.isLoading.value = true;
        try {
            if (page) {
                query.lastId = page.lastId;
                query.lastCity = page.lastCity;
                query.lastRegion = page.lastRegion;
                query.lastCountry = page.lastCountry;
            }

            const locationQueryService = await context.getService<ILocationQueryService>('ILocationQueryService');
            const result = await locationQueryService.findLocations(query);

            if (result && result.results) {
                query.lastCity = undefined;
                data.currentQuery.value = query;

                data.locations.value = result;
                data.locationsCount.value = result.count ?? 0;
            }
            if (!result) {
                showMessage("An error occured while querying account locations.", "danger", true, true);
            }
        } catch (err) {
            console.error("Error querying account locations.");
            showMessage("An error occured while querying account locations.", "danger", true, true);
        } finally {
            data.isLoading.value = false;
        }
    }
}

export default defineComponent({
    name: "CompanyInformation",
    props: {
        account: {
            type: Object as () => AccountModel,
            required: true
        },
        locations: {
            type: Object as () => ArrayResultModel<LocationModel>,
            required: true
        }
    },
    components: {
        'edit-company-address': editCompanyAddress,
        'edit-company-info': editCompanyInfo,
        'edit-fleet-info': editFleetInfo
    },
    setup: function (props: any, ctx: any) {
        let data = getInitialData(props);
        let methods = getMethods(data, props);
        let computed = getComputed(data);

        return {
            ...isGeneralAdmin,
            ...data,
            ...methods,
            ...computed
        };
    }
});